/**
 * Запуск заказа обучения (training-order-entry-points; ТЗ 26.10.2026, п.2.2, п.5.1; ответ заказчицы
 * В22) — общий для всех точек заказа: главная («Требует внимания», «Ближайшие сроки»), раздел
 * «Обучение», «Мои заказы» — «Новый заказ», карточка сотрудника («Обучить сотрудника»).
 *
 * Если есть кого подставить (просрочено или истекает в ближайшие 90 дней — как «Требует
 * внимания»), система спрашивает «Заполнить по сотрудникам с истекающим и просроченным обучением?
 * Заявку можно будет отредактировать». «Да» — черновик заполняется по срокам, «Нет» — обычная
 * заявка. Из карточки сотрудника — по его программам; в любом случае сотрудник ещё и сам
 * добавляется в каждую программу, которую клиент выберет в каталоге. Мастер получает, что
 * сделано, через состояние перехода (`TrainingStartState`).
 */
import { useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ordersApi, trainingApi, type TrainingPrefill } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'

/** Текст вопроса — дословно из ТЗ (п.2.2). */
export const PREFILL_QUESTION =
  'Заполнить по сотрудникам с истекающим и просроченным обучением? Заявку можно будет отредактировать'
/** Тот же вопрос физлицу — о его собственном обучении (individual-training-order). */
export const PREFILL_QUESTION_INDIVIDUAL =
  'Заполнить по вашему истекающему и просроченному обучению? Заявку можно будет отредактировать'
/** Сотрудник, которого обучить (карточка сотрудника). */
export interface TrainingStartPerson {
  id: string
  fullName: string
}

/**
 * Состояние перехода в мастер: что подставлено по срокам (`prefill`), не удалось заполнить
 * (`prefillFailed`) или какой сотрудник добавляется в выбранные программы (`presetPerson`).
 */
export interface TrainingStartState {
  /** Заказ, к которому относится состояние: на другом заказе оно не действует. */
  orderId?: string
  prefill?: Omit<TrainingPrefill, 'wizard'>
  prefillFailed?: boolean
  presetPerson?: TrainingStartPerson
}

function pluralRu(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10
  const m100 = n % 100
  if (m10 === 1 && m100 !== 11) return one
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few
  return many
}

/** «2 сотрудника, 3 программы» — что подставится. */
export function prefillSummaryText(p: Pick<TrainingPrefill, 'persons' | 'programs'>): string {
  return `${p.persons} ${pluralRu(p.persons, 'сотрудник', 'сотрудника', 'сотрудников')}, ${p.programs} ${pluralRu(p.programs, 'программа', 'программы', 'программ')}`
}

/** «Программа «…» больше не в каталоге — выберите вручную» (ответ В22, план MR-8). */
export function unavailableProgramText(name: string): string {
  return `Программа «${name}» больше не в каталоге — выберите вручную`
}

/**
 * Запуск заказа обучения: `start()` — общий, `start(person)` — из карточки сотрудника. Пока идёт
 * запрос, `starting` блокирует кнопки (повторный щелчок отсекает ref); `dialog` — окно вопроса,
 * его рендерит вызывающий. `individual` — физлицо: вопрос о его собственном обучении.
 */
export function useTrainingOrderStart(individual = false): {
  start: (person?: TrainingStartPerson) => void
  starting: boolean
  error: string | null
  dialog: ReactNode
} {
  const navigate = useNavigate()
  const busy = useRef(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ask, setAsk] = useState<{ person?: TrainingStartPerson; summary: TrainingPrefill } | null>(
    null,
  )

  function finish() {
    busy.current = false
    setStarting(false)
  }

  async function create(
    person: TrainingStartPerson | undefined,
    prefill: boolean,
    summary?: TrainingPrefill | null,
  ) {
    setStarting(true)
    let orderId: string
    try {
      orderId = (await ordersApi.create({ service: 'training' })).id
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось создать заявку на обучение'))
      finish()
      return
    }
    const state: TrainingStartState = { orderId }
    // Подставлять некого, но есть что сказать (программы не из каталога, уже заказанное) — без
    // вопроса, но с пояснением над заявкой.
    if (!prefill && summary && (summary.unavailable.length > 0 || summary.alreadyOrdered > 0)) {
      state.prefill = {
        persons: 0,
        programs: 0,
        pairs: 0,
        unavailable: summary.unavailable,
        alreadyOrdered: summary.alreadyOrdered,
      }
    }
    if (prefill) {
      try {
        const res = await trainingApi.prefill(orderId, person?.id)
        state.prefill = {
          persons: res.persons,
          programs: res.programs,
          pairs: res.pairs,
          unavailable: res.unavailable,
          alreadyOrdered: res.alreadyOrdered,
        }
      } catch {
        // Черновик уже есть — открываем его, а не теряем: клиент выберет программы сам.
        state.prefillFailed = true
      }
    }
    // Из карточки сотрудника — он добавляется и в программы, которые клиент выберет сам поверх
    // заполненного по срокам (ТЗ п.5.1).
    if (person) state.presetPerson = person
    navigate(`/orders/${orderId}/training`, { state })
  }

  async function start(person?: TrainingStartPerson) {
    if (busy.current) return
    busy.current = true
    setError(null)
    setStarting(true)
    let summary: TrainingPrefill | null = null
    try {
      summary = await trainingApi.prefillSummary(person?.id)
    } catch {
      // Сводка не загрузилась — заказ всё равно можно оформить обычной заявкой.
    }
    if (summary && summary.pairs > 0) {
      setAsk({ person, summary })
      setStarting(false)
      return
    }
    await create(person, false, summary)
  }

  function answer(prefill: boolean) {
    if (!ask) return
    const { person } = ask
    setAsk(null)
    void create(person, prefill)
  }

  function cancel() {
    setAsk(null)
    finish()
  }

  const dialog = (
    <ConfirmDialog
      open={ask !== null}
      title="Заказ обучения"
      message={individual ? PREFILL_QUESTION_INDIVIDUAL : PREFILL_QUESTION}
      details={
        ask ? (
          <div className="text-13 text-txt-secondary">
            <p>
              {ask.person ? `${ask.person.fullName}: ` : ''}
              {ask.person || individual
                ? `${ask.summary.programs} ${pluralRu(ask.summary.programs, 'программа', 'программы', 'программ')}`
                : prefillSummaryText(ask.summary)}
              .
            </p>
            {ask.summary.unavailable.length > 0 ? (
              <ul className="mt-1 list-disc pl-5">
                {ask.summary.unavailable.map((name) => (
                  <li key={name}>{unavailableProgramText(name)}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : undefined
      }
      buttons={[
        { label: 'Да, заполнить', variant: 'primary', onClick: () => answer(true) },
        { label: 'Нет, обычная заявка', variant: 'secondary', onClick: () => answer(false) },
      ]}
      onClose={cancel}
    />
  )

  return { start: (person) => void start(person), starting, error, dialog }
}

/**
 * После запуска заказа (ответ заказчицы 04.11 к п.4.1–4.3): что подставлено по срокам, что выбрать
 * вручную, что уже заказано и не добавлено, а также «не удалось заполнить» — ВСПЛЫВАЮЩИМ окном с
 * кнопкой «ОК» один раз, на странице это уведомление не висит. Состояние перехода мастер читает
 * один раз, поэтому окно не повторяется при обновлении страницы.
 *
 * Сотрудник по умолчанию (карточка сотрудника, «Обучить сотрудника») — плашкой над мастером: это
 * правило работает всё время, пока выбираются программы.
 */
export function TrainingStartNotice({
  state,
  className,
}: {
  state: TrainingStartState
  className?: string
}) {
  const p = state.prefill
  const hasPopup = Boolean(p || state.prefillFailed)
  const [popupOpen, setPopupOpen] = useState(hasPopup)
  const closePopup = () => setPopupOpen(false)

  let popup: ReactNode = null
  if (hasPopup) {
    const failed = Boolean(state.prefillFailed)
    const filled = Boolean(p && p.pairs > 0)
    popup = (
      <ConfirmDialog
        open={popupOpen}
        title={
          failed
            ? 'Заявку не удалось заполнить'
            : filled
              ? 'Заявка заполнена по срокам обучения'
              : 'Заполнение по срокам обучения'
        }
        message={
          failed
            ? 'Не удалось заполнить заявку по срокам обучения — выберите программы и сотрудников.'
            : filled && p
              ? `Добавлено: ${prefillSummaryText(p)}. Проверьте состав — сотрудников и программы можно добавить или убрать.`
              : 'Проверьте заявку — программы и сотрудников можно добавить или убрать.'
        }
        details={
          !failed && p && (p.unavailable.length > 0 || p.alreadyOrdered > 0) ? (
            <div className="flex flex-col gap-1 text-txt">
              {p.unavailable.length > 0 ? (
                <ul className="list-disc pl-5">
                  {p.unavailable.map((name) => (
                    <li key={name}>{unavailableProgramText(name)}</li>
                  ))}
                </ul>
              ) : null}
              {p.alreadyOrdered > 0 ? (
                <p>
                  {p.pairs > 0 ? 'Ещё ' : ''}
                  {p.alreadyOrdered}{' '}
                  {pluralRu(
                    p.alreadyOrdered,
                    'обучение уже заказано',
                    'обучения уже заказаны',
                    'обучений уже заказаны',
                  )}{' '}
                  в отправленных заказах — их в заявку не добавили.
                </p>
              ) : null}
            </div>
          ) : undefined
        }
        buttons={[{ label: 'ОК', variant: 'primary', onClick: closePopup }]}
        onClose={closePopup}
      />
    )
  }

  const person = state.presetPerson ? (
    <Alert variant="info" className={className}>
      Сотрудник «{state.presetPerson.fullName}» добавляется в каждую программу, которую вы выберете
      в каталоге.
    </Alert>
  ) : null

  if (!popup && !person) return null
  return (
    <>
      {popup}
      {person}
    </>
  )
}
